import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import SearchHotelsView from "../views/SearchHotelsView.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: SearchHotelsView,
    meta: {
      title: "Roomsvile - Book Hotels, Short Lets & Apartments in Nigeria",
      description:
        "Book hotels, short let and serviced apartments across Nigeria with Roomsvile. Find places to stay in Lagos, Abuja, Port Harcourt and every state at the best prices.",
    },
  },
  {
    path: "/search",
    redirect: "/",
  },
  {
    path: "/property/:id",
    name: "hotel-details",
    component: () =>
      import(
        /* webpackChunkName: "hotel-details" */ "../views/HotelDetailsView.vue"
      ),
    meta: {
      title: "Hotel & Apartment Details - Roomsvile",
      description:
        "See photos, facilities, reviews and prices, then book your stay on Roomsvile.",
    },
  },
  {
    path: "/property/:propertyId/rooms",
    name: "property-rooms",
    component: () =>
      import(
        /* webpackChunkName: "property-rooms" */ "../views/RoomsListView.vue"
      ),
    meta: {
      title: "Available Rooms - Roomsvile",
      description:
        "Compare available rooms, prices and amenities, then book instantly on Roomsvile.",
    },
  },
  {
    path: "/room/:id",
    name: "room-details",
    component: () =>
      import(
        /* webpackChunkName: "room-details" */ "../views/RoomDetailsView.vue"
      ),
    meta: {
      title: "Room Details - Roomsvile",
      description:
        "See room photos, beds, guest capacity, facilities and price, then book on Roomsvile.",
    },
  },
  {
    path: "/about",
    name: "about",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
    meta: {
      title: "About Us - Roomsvile",
      description:
        "Learn about Roomsvile, Nigeria's platform for booking hotels, short lets and serviced apartments.",
    },
  },
  {
    path: "/contact",
    name: "contact",
    component: () =>
      import(/* webpackChunkName: "contact" */ "../views/ContactView.vue"),
    meta: {
      title: "Contact Us - Roomsvile",
      description:
        "Get in touch with Roomsvile. We're here to help with all your hotel booking needs",
    },
  },
  {
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/LoginView.vue"),
    meta: {
      noindex: true,
      title: "Log In - Roomsvile",
      description: "Log in to your Roomsvile account to manage your bookings.",
      guestOnly: true,
    },
  },
  {
    path: "/register",
    name: "register",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/RegisterView.vue"),
    meta: {
      noindex: true,
      title: "Create an Account - Roomsvile",
      description:
        "Create a Roomsvile account to book hotels and manage your reservations.",
      guestOnly: true,
    },
  },
  {
    path: "/verify-otp",
    name: "verify-otp",
    component: () =>
      import(/* webpackChunkName: "verify-otp" */ "../views/VerifyOtpView.vue"),
    meta: {
      noindex: true,
      title: "Verify Your Account - Roomsvile",
      description: "Enter the verification code sent to your email.",
      guestOnly: true,
    },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () =>
      import(
        /* webpackChunkName: "forgot-password" */ "../views/ForgotPasswordView.vue"
      ),
    meta: {
      noindex: true,
      title: "Forgot Password - Roomsvile",
      description: "Reset the password for your Roomsvile account.",
      guestOnly: true,
    },
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () =>
      import(
        /* webpackChunkName: "reset-password" */ "../views/ResetPasswordView.vue"
      ),
    meta: {
      noindex: true,
      title: "Reset Password - Roomsvile",
      description: "Set a new password for your Roomsvile account.",
      guestOnly: true,
    },
  },
  {
    path: "/profile",
    name: "profile",
    component: () =>
      import(/* webpackChunkName: "profile" */ "../views/ProfileView.vue"),
    meta: {
      noindex: true,
      title: "My Account - Roomsvile",
      description: "Manage your Roomsvile profile and account settings.",
      requiresAuth: true,
    },
  },
  {
    path: "/verifypayment",
    name: "verify-payment",
    component: () =>
      import(
        /* webpackChunkName: "verify-payment" */ "../views/VerifyPaymentView.vue"
      ),
    meta: {
      noindex: true,
      title: "Verifying Payment - Roomsvile",
      description: "Confirming your booking payment.",
    },
  },
  {
    path: "/bookings",
    name: "bookings",
    component: () =>
      import(/* webpackChunkName: "bookings" */ "../views/MyBookingsView.vue"),
    meta: {
      noindex: true,
      title: "My Bookings - Roomsvile",
      description: "View and manage your room bookings.",
      requiresAuth: true,
    },
  },
  {
    path: "/favourites",
    name: "favourites",
    component: () =>
      import(
        /* webpackChunkName: "favourites" */ "../views/FavouritesView.vue"
      ),
    meta: {
      noindex: true,
      title: "My Favourites - Roomsvile",
      description: "View and manage your saved properties.",
      requiresAuth: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () =>
      import(/* webpackChunkName: "not-found" */ "../views/NotFoundView.vue"),
    meta: {
      noindex: true,
      title: "Page Not Found - Roomsvile",
      description: "The page you're looking for doesn't exist.",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Auth guard: protect routes that require a session, and keep logged-in
// users away from guest-only pages like /login and /register
router.beforeEach((to, from, next) => {
  const isAuthenticated = !!localStorage.getItem("authToken");

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ path: "/login", query: { redirect: to.fullPath } });
    return;
  }

  if (to.meta.guestOnly && isAuthenticated) {
    next("/profile");
    return;
  }

  next();
});

export default router;
